import { notifyManager, useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';

import { getListQueryPage } from './liveLogs';

export interface LiveLogsQueryStatus {
  /** A getList of the resource is in flight. */
  isFetching: boolean;
  /** When the active list last received data, in ms since the epoch; 0 before the first response. */
  updatedAt: number;
  /** The page the active list shows, once it has mounted. */
  page: number | undefined;
}

/**
 * The state of the active `[resource, 'getList']` query, for the toolbar that sits outside the
 * List and its context. It is read from the react-query cache react-admin uses, rather than
 * mirrored into state, so it cannot drift from what the List shows.
 */
export const useLiveLogsQueryStatus = (resource: string): LiveLogsQueryStatus => {
  const queryCache = useQueryClient().getQueryCache();
  const subscribe = useCallback(
    (onStoreChange: () => void) => queryCache.subscribe(notifyManager.batchCalls(onStoreChange)),
    [queryCache],
  );
  const activeQueries = () => queryCache.findAll({ queryKey: [resource, 'getList'], type: 'active' });

  const updatedAt = useSyncExternalStore(subscribe, () =>
    activeQueries().reduce((latest, query) => Math.max(latest, query.state.dataUpdatedAt), 0),
  );
  const page = useSyncExternalStore(subscribe, () => {
    const [active] = activeQueries();
    return active ? getListQueryPage(active.queryKey) : undefined;
  });
  const isFetching = useIsFetching({ queryKey: [resource, 'getList'] }) > 0;

  return { isFetching, updatedAt, page };
};
